'use client';

import { useEffect, useState, useCallback } from 'react';
import posthog from 'posthog-js';

// Feature flag keys - centralized for type safety
export const FEATURE_FLAGS = {
  PLAID_INTEGRATION: 'plaid_integration_enabled',
  PLAID_INCOME_VERIFICATION: 'plaid_income_verification_enabled',
  USE_CASE_CONTAINER: 'use-case-container',
  CASE_LIBRARY: 'case-library-enabled',
  // A/B test experiments (must match PostHog feature flag names)
  HERO_HEADLINE_VARIANT: 'hero-headline-variant',
  HERO_CTA_VARIANT: 'hero-cta-variant',
  // Pilot programs
  HHD_PILOT: 'hhd-pilot',
  // M#116 pricing experiment (#1481): multivariate — arms per_case | flat_annual
  FIRM_PRICING_EXPERIMENT: 'firm-pricing-experiment',
} as const;

export type FeatureFlagKey = typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS];

// Environment variable overrides
const ENV_OVERRIDES: Partial<Record<FeatureFlagKey, boolean>> = {
  [FEATURE_FLAGS.PLAID_INTEGRATION]: import.meta.env.VITE_FF_PLAID_ENABLED === 'true',
  // Income verification is disabled by default until Plaid approves the product
  [FEATURE_FLAGS.PLAID_INCOME_VERIFICATION]: import.meta.env.VITE_FF_PLAID_INCOME_ENABLED === 'true',
  // HHD pilot — set VITE_FF_HHD_PILOT=true in .env or CI to enable without PostHog
  [FEATURE_FLAGS.HHD_PILOT]: import.meta.env.VITE_FF_HHD_PILOT === 'true',
};

/**
 * Check if PostHog is available and loaded
 */
function isPostHogReady(): boolean {
  return typeof window !== 'undefined' && posthog.__loaded;
}

/**
 * Hook to check if a feature flag is enabled
 * Handles loading state and real-time updates from PostHog
 * Falls back to environment variable if PostHog unavailable
 */
export function useFeatureFlag(flagKey: FeatureFlagKey): {
  enabled: boolean;
  isLoading: boolean;
} {
  // Check if this flag has an env override (key exists in ENV_OVERRIDES)
  const hasEnvOverride = flagKey in ENV_OVERRIDES;
  const envValue = hasEnvOverride ? ENV_OVERRIDES[flagKey]! : false;

  const [enabled, setEnabled] = useState<boolean>(() => {
    if (hasEnvOverride) return envValue;
    return false;
  });
  const [isLoading, setIsLoading] = useState(!hasEnvOverride);

  const checkFlag = useCallback(() => {
    // Environment override is FINAL — never let PostHog override it
    if (hasEnvOverride) {
      setEnabled(envValue);
      setIsLoading(false);
      return;
    }

    // Check PostHog only when no env override
    if (isPostHogReady()) {
      const value = posthog.isFeatureEnabled(flagKey);
      setEnabled(value ?? false);
      setIsLoading(false);
    }
  }, [flagKey, hasEnvOverride, envValue]);

  useEffect(() => {
    // Check initial value
    if (isPostHogReady()) {
      checkFlag();
    } else {
      // Wait for PostHog to load feature flags
      posthog.onFeatureFlags(() => {
        checkFlag();
      });
    }

    // Subscribe to feature flag updates
    const unsubscribe = posthog.onFeatureFlags(() => {
      checkFlag();
    });

    // Set a timeout to stop loading even if PostHog never loads
    const timeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
      }
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [flagKey, checkFlag, isLoading]);

  return { enabled, isLoading };
}

/**
 * Convenience hook specifically for Plaid integration
 */
export function usePlaidEnabled(): {
  enabled: boolean;
  isLoading: boolean;
} {
  return useFeatureFlag(FEATURE_FLAGS.PLAID_INTEGRATION);
}

/**
 * Convenience hook specifically for Plaid Income Verification (Payroll)
 * Income verification requires separate Plaid approval
 */
export function usePlaidIncomeEnabled(): {
  enabled: boolean;
  isLoading: boolean;
} {
  return useFeatureFlag(FEATURE_FLAGS.PLAID_INCOME_VERIFICATION);
}

/**
 * Non-hook function to check feature flag synchronously
 * Use only when hooks aren't available
 */
export function isFeatureFlagEnabled(flagKey: FeatureFlagKey): boolean {
  // Environment override first
  if (ENV_OVERRIDES[flagKey] !== undefined) {
    return ENV_OVERRIDES[flagKey]!;
  }

  // Check PostHog
  if (isPostHogReady()) {
    return posthog.isFeatureEnabled(flagKey) ?? false;
  }

  return false;
}

/**
 * Hook for A/B test experiments that return variant strings
 * Returns the variant name (e.g., 'control', 'variant-a', 'variant-b')
 */
export function useExperimentVariant(flagKey: FeatureFlagKey): {
  variant: string | null;
  isLoading: boolean;
} {
  const [variant, setVariant] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkVariant = useCallback(() => {
    if (isPostHogReady()) {
      const value = posthog.getFeatureFlag(flagKey);
      setVariant(typeof value === 'string' ? value : null);
      setIsLoading(false);
    }
  }, [flagKey]);

  useEffect(() => {
    if (isPostHogReady()) {
      checkVariant();
    } else {
      posthog.onFeatureFlags(() => {
        checkVariant();
      });
    }

    const unsubscribe = posthog.onFeatureFlags(() => {
      checkVariant();
    });

    const timeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
      }
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [flagKey, checkVariant, isLoading]);

  return { variant, isLoading };
}

// Hero A/B test variants
export type HeroHeadlineVariant = 'control' | 'protect-assets' | 'save-money';
export type HeroCTAVariant = 'control' | 'see-strategy' | 'get-started';

/**
 * Hook for Hero headline A/B test
 */
export function useHeroHeadlineVariant(): {
  variant: HeroHeadlineVariant;
  isLoading: boolean;
} {
  const { variant, isLoading } = useExperimentVariant(FEATURE_FLAGS.HERO_HEADLINE_VARIANT);
  return {
    variant: (variant as HeroHeadlineVariant) || 'control',
    isLoading,
  };
}

/**
 * Convenience hook for HHD Pilot feature flag
 * Controls progressive sidebar disclosure and other pilot behaviors
 */
export function useHHDPilot(): {
  enabled: boolean;
  isLoading: boolean;
} {
  return useFeatureFlag(FEATURE_FLAGS.HHD_PILOT);
}

/**
 * Hook for Hero CTA A/B test
 */
export function useHeroCTAVariant(): {
  variant: HeroCTAVariant;
  isLoading: boolean;
} {
  const { variant, isLoading } = useExperimentVariant(FEATURE_FLAGS.HERO_CTA_VARIANT);
  return {
    variant: (variant as HeroCTAVariant) || 'control',
    isLoading,
  };
}
